import { AttributionService } from '@apparel/ui/lib/AttributionService';
import { GoogleAnalyticsService } from '@apparel/ui/lib/GoogleAnalyticService';
import { GoogleTagService } from '@apparel/ui/lib/GoogleTag';
import { GOOGLE_ANALYTIC_SERVICE, GTag } from '@apparel/ui/lib/utils';
import { LoadingButton } from '@components/Buttons';
import { Col } from '@components/Layout/Col';
import { Container } from '@components/Layout/Container';
import { Row } from '@components/Layout/Row';
import { SPaper } from '@components/SPaper';
import { Trans } from '@components/Trans';
import { api } from '@lib/http';
import { setToken, setUser } from '@lib/jwt';
import { getSubdomainData } from '@lib/utils';
import { withErrorHandling } from '@lib/withErrorHandling';
import Button from '@material-ui/core/Button/Button';
import Typography from '@material-ui/core/Typography/Typography';
import { CustomerRegistrationValidation } from '@model/Customer';
import { ROLES } from '@model/Role';
import { Form, Formik } from 'formik';
import React, { useContext, useState } from 'react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import LoginForm from '../../Customers/components/LoginForm';
import { InstantQuoteContext } from './InstantQuoteStepper';

const Style = styled(Container)`
    && {
        padding-bottom: 10px;

        .divider-right {
            position: relative;

            &:after {
                content: ' ';
                position: absolute;
                top: 28px;
                bottom: 14px;
                right: 0;
                width: 1px;
                background-color: #ccc;
            }
        }

        .black-button {
            background-color: #424242;
            color: #fff;
            font-weight: 100;
            padding: 10px 40px;
            margin-top: 16px;
            margin-left: 14px;
        }
    }
`;

interface IIQLoginFormProps {
    allowContinueAsGuest: boolean;
    redirectAfterLogin: string;
}

const IQLoginForm = (props: IIQLoginFormProps | any) => {
    const [customer, setCustomer] = useState({
        user: { first_name: '', last_name: '', email: '' },
        organization_name: '',
        password: '',
    });
    const { instantQuote, dispatch } = useContext<any>(InstantQuoteContext);

    const handleOnSubmit = (values, actions) => {
        const referral_code = localStorage.getItem('referral');
        const subdomainData = getSubdomainData();
        const registerData = {
            ...values.user,
            wholesaler_id: subdomainData?.id,
            referral_code,
            preselected_role: ROLES.CUSTOMER,
            password_confirmation: values.user.password,
            no_progress: true,
            ...AttributionService.getForSignup(),
        };

        api.post('/register', registerData)
            .then((response) => {
                const user = response.data.data;

                setToken(response.data.token);
                setUser(user);

                setCustomer(values);
                actions.setSubmitting(false);
                handleRedirectToSportSelection();
                localStorage.removeItem('referral');
                AttributionService.clear();

                // That page appears either on beginning or end.
                // If beginning => just set the customer on order-obj
                // If end => send the store request
                const dispatchType = props.allowContinueAsGuest ? 'setOrder' : 'createOrder';
                GoogleTagService.reportConversion(GTag.SignUp);
                GoogleAnalyticsService.reportConversion(GOOGLE_ANALYTIC_SERVICE.NEW_FORM_SUBMISSION);
                dispatch({
                    type: dispatchType,
                    payload: {
                        ...instantQuote.order,
                        customer: {
                            ...values,
                            user: {
                                // Throw away all the other stuff, there's a lot in the response
                                id: user.id,
                                first_name: user.first_name,
                                last_name: user.last_name,
                                email: user.email,
                                phone: user.phone,
                            },
                        },
                    },
                });
            })
            .catch((error) => {
                console.error(error);
                props.showError(error.response.data);
                actions.setSubmitting(false);
            });
    };

    const handleRedirectToSportSelection = () => {
        if (props.redirectAfterLogin) {
            props.history.push(props.redirectAfterLogin);
        }
    };

    return (
        <Style>
            <Row>
                {props.allowContinueAsGuest ? null : <Col xs={3}>&nbsp;</Col>}
                <Col xs={12} md={props.allowContinueAsGuest ? 12 : 6}>
                    <SPaper>
                        <Row>
                            <Col
                                xs={12}
                                md={props.allowContinueAsGuest ? 6 : 12}
                                className={props.allowContinueAsGuest ? 'divider-right' : ''}
                            >
                                <Typography variant={'h6'} style={{ margin: '14px' }}>
                                    Create an Account to Get Your Quote
                                </Typography>
                                <Formik
                                    initialValues={customer}
                                    validationSchema={CustomerRegistrationValidation}
                                    onSubmit={handleOnSubmit}
                                >
                                    {({ isSubmitting }) => (
                                        <Form>
                                            <LoginForm />

                                            <Row>
                                                <Col
                                                    xs={12}
                                                    md={12}
                                                    justify={'end'}
                                                    align={'center'}
                                                    style={{ marginTop: 14, marginBottom: 14 }}
                                                >
                                                    <LoadingButton
                                                        variant={'contained'}
                                                        color={'primary'}
                                                        type={'submit'}
                                                        loading={isSubmitting || instantQuote.creatingOrder}
                                                        fullWidth={true}
                                                    >
                                                        <Trans>Create Account</Trans>
                                                    </LoadingButton>
                                                </Col>
                                                {props.allowContinueAsGuest ? (
                                                    <Col
                                                        xs={12}
                                                        md={12}
                                                        justify={'end'}
                                                        align={'center'}
                                                        style={{ marginTop: 0, marginBottom: 14 }}
                                                    >
                                                        <Button
                                                            component={Link}
                                                            to={'/sign-in'}
                                                            variant={'outlined'}
                                                            color={'default'}
                                                            fullWidth={true}
                                                            size={'small'}
                                                        >
                                                            <Trans>Already have an account? Go to Login</Trans>
                                                        </Button>
                                                    </Col>
                                                ) : null}
                                            </Row>
                                        </Form>
                                    )}
                                </Formik>
                            </Col>
                            {props.allowContinueAsGuest ? (
                                <Col xs={12} md={6} direction={'column'} align={'top'}>
                                    <Typography variant={'h6'} style={{ margin: '14px' }}>
                                        Or:
                                    </Typography>
                                    <Button
                                        variant={'contained'}
                                        className={'black-button'}
                                        onClick={handleRedirectToSportSelection}
                                    >
                                        Continue as a Guest
                                    </Button>
                                </Col>
                            ) : null}
                        </Row>
                    </SPaper>
                </Col>
            </Row>
        </Style>
    );
};

export default withRouter(withErrorHandling(IQLoginForm)) as any;
